import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Empty, NativeSelect } from "@/components/common";
import { ObligationDialog } from "@/components/docs/ObligationDialog";
import { useDocsMeta } from "@/pages/Documenti";
import { api, errMsg } from "@/lib/api";
import { fmtD, fmtDT } from "@/lib/format";
import { cn } from "@/lib/utils";

const TABS = [["bozza", "Da convalidare"], ["attivo", "Scadenzario"], ["rischi", "Rischi"], ["adempiuto", "Adempiuti"], ["scartato", "Scartati"]];

const Stat = ({ label, value, tone, testid }) => (
  <div className={cn("card-soft p-4", tone)} data-testid={testid}><p className="text-2xl font-extrabold">{value ?? "—"}</p><p className="text-xs font-semibold text-muted-foreground">{label}</p></div>
);

const Risk = ({ r, reload }) => {
  const [note, setNote] = useState("");
  const verify = async (outcome) => {
    try {
      await api.post(`/obligations/risks/${r.id}/verify`, { outcome, note });
      toast.success("Rischio verificato");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="card-soft border-l-4 border-l-red-500 p-5" data-testid={`risk-${r.id}`}>
      <p className="flex items-center gap-2 font-bold"><AlertTriangle className="h-4 w-4 text-red-600" />{r.obligation_code} · {r.obligation_title}</p>
      <p className="mt-1 text-sm">Periodo del {fmtD(r.period)}: {r.reason}. <b>Rischio potenziale da verificare.</b></p>
      {r.penalty_text && <p className="mt-1 text-sm text-red-700">Penale: {r.penalty_text}</p>}
      {r.doc_title && <p className="text-xs text-muted-foreground">Fonte: {r.doc_title}, p. {r.page}</p>}
      {r.status === "aperto" && r.can_manage ? (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Esito della verifica (obbligatorio)" className="rounded-xl" data-testid={`risk-note-${r.id}`} />
          {[["confermato", "Confermato"], ["risolto", "Risolto"], ["infondato", "Infondato"]].map(([k, l]) => <Button key={k} variant="outline" disabled={note.length < 3} onClick={() => verify(k)} className="rounded-xl" data-testid={`risk-${k}-${r.id}`}>{l}</Button>)}
        </div>
      ) : r.status !== "aperto" && <p className="mt-2 text-xs font-semibold">{r.status} · {r.verified_by_name} · {fmtDT(r.verified_at)} · {r.verify_note}</p>}
    </div>
  );
};

const ObRow = ({ o, onOpen }) => (
  <button onClick={() => onOpen(o)} className={cn("card-soft flex w-full flex-col gap-1 p-4 text-left hover:bg-accent/30 sm:flex-row sm:items-center sm:gap-4", o.overdue && "ring-2 ring-red-500")} data-testid={`obl-row-${o.code}`}>
    <div className="min-w-0 flex-1">
      <p className="font-bold"><span className="mr-2 font-mono text-xs text-muted-foreground">{o.code}</span>{o.title}</p>
      <p className="truncate text-xs text-muted-foreground">{o.frequency_label}{o.deadline_text && ` · ${o.deadline_text}`}{o.penalty_text && " · penale prevista"}{o.page && ` · ${o.doc_title}, p. ${o.page}`}</p>
    </div>
    <p className={cn("shrink-0 text-sm font-semibold", o.overdue && "text-red-600")}>{o.next_due ? fmtD(o.next_due) : "—"}</p>
  </button>
);

export default function Obblighi() {
  const meta = useDocsMeta();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "bozza";
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [riskStatus, setRiskStatus] = useState("aperto");
  const load = useCallback(() => {
    api.get("/obligations/stats").then((r) => setStats(r.data));
    const req = tab === "rischi" ? api.get("/obligations/risks", { params: { status: riskStatus || undefined } })
      : api.get("/obligations", { params: { status: tab, q: q || undefined, doc_id: sp.get("doc") || undefined } });
    req.then((r) => setRows(r.data));
  }, [tab, q, sp, riskStatus]);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div data-testid="obblighi-page">
      <PageHeader title="Obblighi contrattuali" subtitle="Estratti da Toth, convalidati dalle persone · scadenzario e rischi" />
      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Bozze da convalidare" value={stats?.bozze} testid="obl-stat-bozze" />
        <Stat label="Attivi" value={stats?.attivi} testid="obl-stat-attivi" />
        <Stat label="In scadenza 30 giorni" value={stats?.in_scadenza_30g} testid="obl-stat-soon" />
        <Stat label="Scaduti" value={stats?.scaduti} tone={stats?.scaduti ? "text-red-600" : ""} testid="obl-stat-scaduti" />
        <Stat label="Rischi aperti" value={stats?.rischi_aperti} tone={stats?.rischi_aperti ? "text-red-600" : ""} testid="obl-stat-rischi" />
      </div>
      <Tabs value={tab} onValueChange={(v) => { setRows(null); setSp({ tab: v }); }}>
        <TabsList className="mb-4 h-auto flex-wrap justify-start rounded-xl">{TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`obl-tab-${v}`}>{l}</TabsTrigger>)}</TabsList>
      </Tabs>
      {tab === "rischi" ? (
        <NativeSelect value={riskStatus} onChange={setRiskStatus} className="mb-4" testid="risk-status-filter"><option value="aperto">Aperti</option><option value="">Tutti</option></NativeSelect>
      ) : <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtra per titolo o codice…" className="mb-4 max-w-md rounded-xl" data-testid="obl-filter" />}
      {sp.get("doc") && tab !== "rischi" && <p className="mb-3 text-xs text-muted-foreground">Filtrati per documento · <button className="font-semibold text-primary" onClick={() => setSp({ tab })}>mostra tutti</button></p>}
      {!rows ? <p className="text-muted-foreground">Caricamento…</p> : rows.length === 0 ? <Empty text="Nessun elemento." testid="obl-empty" /> : (
        <div className="space-y-2" data-testid="obl-list">{tab === "rischi" ? rows.map((r) => <Risk key={r.id} r={r} reload={load} />) : rows.map((o) => <ObRow key={o.id} o={o} onOpen={setSel} />)}</div>
      )}
      {sel && meta && <ObligationDialog ob={sel} frequencies={meta.frequencies} onClose={() => setSel(null)} onSaved={load} />}
    </div>
  );
}
